import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const Navbar = ({ auth }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', href: '/' },
    { name: 'Crawls', href: '/crawls' },
    { name: 'About', href: '/about' },
    { name: 'Groups', href: '/groups' },
    { name: 'Contact', href: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  // On crawl detail pages, GET TICKETS should mirror the bottom bar action
  const isCrawlDetail = location.pathname.match(/^\/crawls\/[^/]+$/);

  const handleGetTickets = (e) => {
    if (isCrawlDetail && window.__cbcTicketAction) {
      e.preventDefault();
      window.__cbcTicketAction();
    } else {
      navigate('/crawls');
    }
  };

  return (
    <>
      {/* Desktop Navbar — full bar, hidden on mobile */}
      <nav 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 hidden md:block ${
          scrolled ? 'nav-scrolled' : 'nav-scrolled'
        }`}
        data-testid="navbar"
      >
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-16">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center" data-testid="nav-logo">
              <img 
                src="https://customer-assets.emergentagent.com/job_8d9bacaf-3cbd-4601-a4fa-6b92074da917/artifacts/hvbdh30e_Logo_1.PNG" 
                alt="Crawl Baby Crawl" 
                className="h-[72px] w-auto"
              />
            </Link>
            <div className="flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  data-testid={`nav-link-${link.name.toLowerCase()}`}
                  className={`nav-link ${isActive(link.href) ? 'text-hot-pink' : ''}`}
                >
                  {link.name}
                </Link>
              ))}
            </div>
            <div className="flex items-center gap-4">
              {auth.isAdmin && (
                <Link to="/admin" data-testid="nav-admin-link" className="nav-link text-cyan">
                  Admin
                </Link>
              )}
              <button
                onClick={handleGetTickets}
                data-testid="nav-get-tickets-btn"
                className="btn-primary px-6 py-3"
              >
                GET TICKETS
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile — floating hamburger only, no bar */}
      <div className="md:hidden fixed top-4 left-4 z-50" data-testid="mobile-nav-wrapper">
        <button
          className="w-11 h-11 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center border border-white/15 text-warm-white"
          onClick={() => setIsOpen(!isOpen)}
          data-testid="mobile-menu-btn"
        >
          {isOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="md:hidden fixed inset-0 z-40 bg-[#080010]/95 backdrop-blur-md"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col items-center justify-center h-full gap-6 px-8">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  onClick={() => setIsOpen(false)}
                  data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                  className={`font-nav text-2xl uppercase tracking-[4px] ${
                    isActive(link.href) ? 'text-hot-pink' : 'text-warm-white'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <button
                onClick={(e) => { setIsOpen(false); handleGetTickets(e); }}
                data-testid="mobile-get-tickets-btn"
                className="btn-primary px-10 py-4 mt-4 text-lg"
              >
                GET TICKETS
              </button>
              {auth.isAdmin && (
                <Link
                  to="/admin"
                  onClick={() => setIsOpen(false)}
                  data-testid="mobile-admin-link"
                  className="text-cyan text-lg uppercase tracking-[4px] font-nav mt-2"
                >
                  Admin
                </Link>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
